import { 
  Users, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import type { Agent, Approval, DepartmentMetrics, Escalation } from '@/types/supervisor';

const agents: Agent[] = [];
const escalations: Escalation[] = [];
const approvals: Approval[] = [];
const departmentMetrics: DepartmentMetrics[] = [];
import { cn } from '@/lib/utils';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: 'up' | 'down';
  trendValue?: string;
  variant?: 'default' | 'warning' | 'success' | 'destructive';
}

function MetricCard({ title, value, subtitle, icon: Icon, trend, trendValue, variant = 'default' }: MetricCardProps) {
  return (
    <Card className="border-2">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={cn(
            "p-2 rounded-lg",
            variant === 'warning' && "bg-warning/10",
            variant === 'success' && "bg-success/10",
            variant === 'destructive' && "bg-destructive/10",
            variant === 'default' && "bg-primary/10"
          )}>
            <Icon className={cn(
              "h-5 w-5",
              variant === 'warning' && "text-warning",
              variant === 'success' && "text-success",
              variant === 'destructive' && "text-destructive",
              variant === 'default' && "text-primary"
            )} />
          </div>
        </div>
        {trend && trendValue && (
          <div className="flex items-center gap-1 mt-2">
            {trend === 'up' ? (
              <TrendingUp className="h-3 w-3 text-success" />
            ) : (
              <TrendingDown className="h-3 w-3 text-destructive" />
            )}
            <span className={cn(
              "text-xs font-medium",
              trend === 'up' ? "text-success" : "text-destructive"
            )}>
              {trendValue}
            </span>
            <span className="text-xs text-muted-foreground">vs last week</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function SupervisorDashboard() {
  const onlineAgents = agents.filter(a => a.status === 'online' || a.status === 'busy').length;
  const pendingEscalations = escalations.filter(e => e.status === 'pending').length;
  const pendingApprovals = approvals.filter(a => a.status === 'pending').length;
  const avgSlaCompliance = Math.round(departmentMetrics.reduce((acc, d) => acc + d.slaCompliance, 0) / departmentMetrics.length);

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 animate-fade-in sm:p-6 sm:space-y-6">
      {/* Header */}
      <div className="space-y-0.5 sm:space-y-1">
        <h1 className="text-xl font-bold sm:text-2xl">Supervisor Dashboard</h1>
        <p className="text-sm text-muted-foreground sm:text-base">Real-time overview of team performance and pending actions</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Agents Online"
          value={`${onlineAgents}/${agents.length}`}
          subtitle="Team available now"
          icon={Users}
          variant="success"
        />
        <MetricCard
          title="Pending Escalations"
          value={pendingEscalations}
          subtitle="Require attention"
          icon={AlertTriangle}
          variant={pendingEscalations > 5 ? 'destructive' : 'warning'}
        />
        <MetricCard
          title="Pending Approvals"
          value={pendingApprovals}
          subtitle="Awaiting decision"
          icon={CheckCircle}
          variant="default"
        />
        <MetricCard
          title="SLA Compliance"
          value={`${avgSlaCompliance}%`}
          subtitle="Across all departments"
          icon={Clock}
          trend="up"
          trendValue="+3%"
          variant="success"
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Escalations */}
        <Card className="border-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-warning" />
                Recent Escalations
              </CardTitle>
              <Badge variant="outline" className="text-xs">
                {escalations.filter(e => e.status === 'pending').length} pending
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {escalations.slice(0, 4).map((escalation) => (
              <div 
                key={escalation.id} 
                className="flex items-start justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <div className="space-y-1 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-muted-foreground">{escalation.ticketId}</span>
                    {escalation.isVip && (
                      <Badge className="bg-warning/20 text-warning-foreground text-[10px] px-1 py-0">VIP</Badge>
                    )}
                    {escalation.slaAtRisk && (
                      <Badge variant="destructive" className="text-[10px] px-1 py-0">SLA Risk</Badge>
                    )}
                  </div>
                  <p className="text-sm font-medium truncate">{escalation.ticketTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    by {escalation.escalatedBy} • {escalation.department}
                  </p>
                </div>
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-[10px] shrink-0 ml-2",
                    escalation.status === 'pending' && "border-warning text-warning",
                    escalation.status === 'in_progress' && "border-primary text-primary",
                    escalation.status === 'resolved' && "border-success text-success"
                  )}
                >
                  {escalation.status.replace('_', ' ')}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Agent Workload Overview */}
        <Card className="border-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Agent Workload
              </CardTitle>
              <Badge variant="outline" className="text-xs">
                {onlineAgents} online
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {agents.slice(0, 5).map((agent) => {
              const loadPercentage = (agent.currentLoad / agent.maxCapacity) * 100;
              const isOverloaded = loadPercentage > 80;
              
              return (
                <div key={agent.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        agent.status === 'online' && "bg-success",
                        agent.status === 'busy' && "bg-warning",
                        agent.status === 'away' && "bg-muted-foreground",
                        agent.status === 'offline' && "bg-muted"
                      )} />
                      <span className="text-sm font-medium">{agent.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {agent.currentLoad}/{agent.maxCapacity} tickets
                    </span>
                  </div>
                  <Progress 
                    value={loadPercentage} 
                    className={cn(
                      "h-1.5",
                      isOverloaded && "[&>div]:bg-destructive"
                    )}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Department Overview */}
      <Card className="border-2">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              Department Performance
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {departmentMetrics.map((dept) => (
              <div 
                key={dept.id} 
                className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer space-y-3"
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm">{dept.name}</h4>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-success" />
                    <span className="text-xs text-muted-foreground">{dept.agentsOnline}/{dept.totalAgents}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Open</span>
                    <span className="font-medium">{dept.openTickets}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Pending</span>
                    <span className="font-medium">{dept.pendingTickets}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">SLA</span>
                    <span className={cn(
                      "font-medium",
                      dept.slaCompliance >= 90 ? "text-success" : dept.slaCompliance >= 80 ? "text-warning" : "text-destructive"
                    )}>
                      {dept.slaCompliance}%
                    </span>
                  </div>
                </div>
                {dept.escalatedTickets > 0 && (
                  <Badge variant="outline" className="w-full justify-center text-[10px] border-warning text-warning">
                    {dept.escalatedTickets} escalated
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
